import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Flame, Crown, Trophy, Star, Play, BookOpen, Sparkles } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import TarotCardQuiz from './TarotCardQuiz';
import TarotCardDetail from './TarotCardDetail';
import TarotDeckLibrary from './TarotDeckLibrary';
import TarotSpreadJournal from './TarotSpreadJournal';

export default function TarotLearningHub() {
  const [progress, setProgress] = useState<any>(null);
  const [currentCard, setCurrentCard] = useState<any>(null);
  const [showQuiz, setShowQuiz] = useState(false);
  const [showCardDetail, setShowCardDetail] = useState(false);
  const [showDeckLibrary, setShowDeckLibrary] = useState(false);
  const [showSpreadJournal, setShowSpreadJournal] = useState(false);
  const [weekDays, setWeekDays] = useState<boolean[]>([]);
  const [studiedCards, setStudiedCards] = useState<any[]>([]);
  const [masteredCards, setMasteredCards] = useState<any[]>([]);
  const [showMasteredCards, setShowMasteredCards] = useState(false);
  const [showStudiedCardsList, setShowStudiedCardsList] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadProgress();
    calculateWeekStreak();
  }, []);

  const loadProgress = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Load current progress
    const { data: progressData } = await supabase
      .from('tarot_learning_progress')
      .select('*, tarot_cards(*)')
      .eq('user_id', user.id)
      .maybeSingle();

    // Load all cards up to the current progress for quiz variety
    if (progressData && progressData.tarot_cards) {
      const { data: cardsUpToCurrent } = await supabase
        .from('tarot_cards')
        .select('*')
        .eq('is_published', true)
        .lte('sort_order', progressData.tarot_cards.sort_order)
        .order('sort_order');

      // Load mastered cards (cards before current card)
      const { data: masteredCardsData } = await supabase
        .from('tarot_cards')
        .select('*')
        .eq('is_published', true)
        .lt('sort_order', progressData.tarot_cards.sort_order)
        .order('sort_order');

      setProgress(progressData);
      setCurrentCard(progressData.tarot_cards);
      setStudiedCards(cardsUpToCurrent || [progressData.tarot_cards]);
      setMasteredCards(masteredCardsData || []);
    } else {
      // Initialize progress for new user with first card
      const { data: firstCard } = await supabase
        .from('tarot_cards')
        .select('*')
        .eq('is_published', true)
        .order('sort_order')
        .limit(1)
        .single();

      if (firstCard) {
        const { data: newProgress } = await supabase
          .from('tarot_learning_progress')
          .insert({
            user_id: user.id,
            current_card_id: firstCard.id,
            overall_score: 0,
            weekly_score: 0,
            streak_count: 0,
            cards_mastered: 0
          })
          .select('*, tarot_cards(*)')
          .single();

        setProgress(newProgress);
        setCurrentCard(newProgress?.tarot_cards);
        setStudiedCards([newProgress?.tarot_cards]);
        setMasteredCards([]);
      }
    }
  };

  const calculateWeekStreak = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const today = new Date();
    const daysOfWeek = Array.from({ length: 7 }, (_, i) => {
      const date = new Date(today);
      date.setDate(today.getDate() - today.getDay() + i);
      return format(date, 'yyyy-MM-dd');
    });

    const { data: completions } = await supabase
      .from('tarot_quiz_completions')
      .select('quiz_date')
      .eq('user_id', user.id)
      .gte('quiz_date', daysOfWeek[0])
      .lte('quiz_date', daysOfWeek[6]);

    const completedDates = new Set(completions?.map(c => c.quiz_date) || []);
    setWeekDays(daysOfWeek.map(date => completedDates.has(date)));
  };

  const updateStreak = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || !progress) return;

    const today = format(new Date(), 'yyyy-MM-dd');
    const lastPractice = progress.last_practice_date;

    let newStreak = progress.streak_count;
    if (lastPractice !== today) {
      const yesterday = format(new Date(Date.now() - 86400000), 'yyyy-MM-dd');
      newStreak = lastPractice === yesterday ? newStreak + 1 : 1;

      await supabase
        .from('tarot_learning_progress')
        .update({
          streak_count: newStreak,
          last_practice_date: today,
          overall_score: progress.overall_score + 10,
          weekly_score: progress.weekly_score + 10
        })
        .eq('user_id', user.id);

      loadProgress();
      calculateWeekStreak();
    }
  };

  const startQuiz = () => {
    setShowQuiz(true);
    updateStreak();
  };

  const handleQuizComplete = async (score: number) => {
    setShowQuiz(false);
    toast({
      title: 'Quiz Complete!',
      description: `You scored ${score} points. Keep going!`
    });
    
    // Check if user achieved high score and progress to next card
    const percentScore = score / (studiedCards.length * 20); // Approximate
    if (percentScore >= 0.7) {
      await progressToNextCard();
    }
    
    loadProgress();
    calculateWeekStreak();
  };

  const progressToNextCard = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || !currentCard) return;

    const currentCardName = currentCard.card_name || currentCard.name;
    const currentSuit = currentCard.suit?.toLowerCase() || '';

    // Get next card in sequence
    const { data: nextCard } = await supabase
      .from('tarot_cards')
      .select('*')
      .eq('is_published', true)
      .gt('sort_order', currentCard.sort_order)
      .order('sort_order')
      .limit(1)
      .maybeSingle();

    // Update progress
    const newCardsMastered = (progress?.cards_mastered || 0) + 1;
    
    await supabase
      .from('tarot_learning_progress')
      .update({
        current_card_id: nextCard?.id || currentCard.id,
        cards_mastered: newCardsMastered,
        updated_at: new Date().toISOString()
      })
      .eq('user_id', user.id);

    // Check for milestone completions
    const { data: allCards } = await supabase
      .from('tarot_cards')
      .select('suit, sort_order')
      .eq('is_published', true)
      .lte('sort_order', currentCard.sort_order);

    const suitCounts = {
      major: allCards?.filter(c => c.suit?.toLowerCase().includes('major')).length || 0,
      wands: allCards?.filter(c => c.suit?.toLowerCase() === 'wands').length || 0,
      cups: allCards?.filter(c => c.suit?.toLowerCase() === 'cups').length || 0,
      swords: allCards?.filter(c => c.suit?.toLowerCase() === 'swords').length || 0,
      pentacles: allCards?.filter(c => c.suit?.toLowerCase() === 'pentacles').length || 0,
    };

    // Determine celebration message
    let celebrationTitle = `You've learned ${currentCardName}!`;
    let celebrationMessage = '';

    if (newCardsMastered === 78) {
      celebrationTitle = 'MASTER ACHIEVEMENT UNLOCKED!';
      celebrationMessage = 'You have mastered the complete 78-card Rider-Waite tarot deck! You are now a true Tarot Master!';
    } else if (currentSuit.includes('major') && suitCounts.major === 22) {
      celebrationTitle = 'Major Arcana Complete!';
      celebrationMessage = `You've mastered all 22 Major Arcana cards! ${nextCard ? `Now let's learn ${nextCard.card_name}` : ''}`;
    } else if (currentSuit === 'wands' && suitCounts.wands === 14) {
      celebrationTitle = 'Suit of Wands Complete!';
      celebrationMessage = `You've mastered all 14 Wands! ${nextCard ? `Now let's learn ${nextCard.card_name}` : ''}`;
    } else if (currentSuit === 'cups' && suitCounts.cups === 14) {
      celebrationTitle = 'Suit of Cups Complete!';
      celebrationMessage = `You've mastered all 14 Cups! ${nextCard ? `Now let's learn ${nextCard.card_name}` : ''}`;
    } else if (currentSuit === 'swords' && suitCounts.swords === 14) {
      celebrationTitle = 'Suit of Swords Complete!';
      celebrationMessage = `You've mastered all 14 Swords! ${nextCard ? `Now let's learn ${nextCard.card_name}` : ''}`;
    } else if (currentSuit === 'pentacles' && suitCounts.pentacles === 14) {
      celebrationTitle = 'Suit of Pentacles Complete!';
      celebrationMessage = `You've mastered all 14 Pentacles! ${nextCard ? `Now let's learn ${nextCard.card_name}` : ''}`;
    } else if (nextCard) {
      celebrationMessage = `Great work! Now let's learn ${nextCard.card_name}`;
    } else {
      celebrationMessage = 'Amazing progress!';
    }

    toast({
      title: celebrationTitle,
      description: celebrationMessage,
      duration: 6000
    });
    
    await loadProgress();
  };

  const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <div className="min-h-screen bg-background p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-light tracking-wide bg-gradient-to-r from-primary via-primary-light to-accent bg-clip-text text-transparent">
          Tarot Learning
        </h1>
        <p className="text-muted-foreground font-light">Master the Rider-Waite deck one card at a time</p>
      </div>

      {/* Streak Card */}
      <Card className="bg-gradient-to-br from-primary/5 to-accent/5 border-primary/20">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-primary/20 to-accent/20 rounded-full">
                <Flame className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-light text-primary">{progress?.streak_count || 0} Day Streak</h3>
                <p className="text-xs text-muted-foreground font-light">Keep practicing daily to maintain your streak!</p>
              </div>
            </div>
          </div>
          
          <div className="mb-6 mt-4">
            <p className="text-sm font-light text-foreground mb-3">This Week's Practice</p>
            <div className="flex justify-between gap-2">
              {DAYS.map((day, idx) => (
                <div 
                  key={idx} 
                  className={`flex-1 flex flex-col items-center gap-2 p-3 rounded-lg transition-all ${
                    weekDays[idx] 
                      ? 'bg-gradient-to-br from-primary to-accent shadow-lg shadow-primary/20' 
                      : 'bg-muted/50 border-2 border-dashed border-muted-foreground/20'
                  }`}
                >
                  <div className={`text-xs font-semibold ${
                    weekDays[idx] ? 'text-background' : 'text-muted-foreground'
                  }`}>
                    {day}
                  </div>
                  <div className={`flex items-center justify-center ${
                    weekDays[idx] ? 'text-background' : 'text-muted-foreground/40'
                  }`}>
                    {weekDays[idx] ? (
                      <Flame className="h-5 w-5" />
                    ) : (
                      <div className="h-2 w-2 rounded-full bg-current" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 text-center pt-4 border-t border-primary/10">
            <button 
              onClick={() => setShowMasteredCards(true)}
              className="space-y-1 hover:bg-primary/5 rounded-lg p-2 transition-colors cursor-pointer"
            >
              <div className="flex items-center justify-center gap-1 text-primary">
                <BookOpen className="h-5 w-5" />
                <span className="text-2xl font-bold">{masteredCards.length}</span>
              </div>
              <p className="text-xs text-muted-foreground">Cards Mastered</p>
              <p className="text-[10px] text-muted-foreground/70">tap to view</p>
            </button>
            <button 
              onClick={() => setShowStudiedCardsList(true)}
              className="space-y-1 hover:bg-accent/5 rounded-lg p-2 transition-colors cursor-pointer"
            >
              <div className="flex items-center justify-center gap-1 text-accent">
                <Star className="h-5 w-5" />
                <span className="text-2xl font-bold">{studiedCards.length}</span>
              </div>
              <p className="text-xs text-muted-foreground">Cards Studied</p>
              <p className="text-[10px] text-muted-foreground/70">tap to view</p>
            </button>
            <div className="space-y-1">
              <div className="flex items-center justify-center gap-1 text-primary">
                <Trophy className="h-5 w-5" />
                <span className="text-2xl font-bold">{Math.round(((progress?.cards_mastered || 0) / 78) * 100)}%</span>
              </div>
              <p className="text-xs text-muted-foreground">Completion</p>
              <p className="text-[10px] text-muted-foreground/70">of full deck</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Current Card Progress */}
      {currentCard && (
        <Card className="bg-gradient-to-br from-primary/5 to-accent/10 border-primary/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-light">Currently Learning</h3>
              <Badge variant="secondary" className="bg-primary/10 text-primary border border-primary/20">
                Card {currentCard.card_number} of 78
              </Badge>
            </div>
            
            <Button 
              className="w-full bg-gradient-to-r from-primary to-primary-light hover:from-primary-light hover:to-accent p-4 h-auto"
              size="lg"
              onClick={() => setShowCardDetail(true)}
            >
              <div className="flex items-center gap-4 w-full">
                <div className="w-24 h-36 rounded-lg bg-background/90 flex items-center justify-center overflow-hidden border-2 border-background/40 flex-shrink-0 shadow-lg">
                  {(currentCard.image_url || currentCard.imageUrl) ? (
                    <img 
                      src={currentCard.image_url || currentCard.imageUrl} 
                      alt={currentCard.card_name}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <Sparkles className="h-8 w-8 text-muted-foreground" />
                  )}
                </div>
                <div className="text-left flex-grow space-y-1">
                  <div className="font-light text-xl text-background drop-shadow-md">{currentCard.card_name}</div>
                  <div className="text-sm text-background/90 font-light">Tap to study this card</div>
                  <div className="text-xs text-background/70 font-light">Learn meanings, symbols & interpretations</div>
                </div>
              </div>
            </Button>

            <div className="mt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Deck Mastery Progress</span>
                <span className="font-light text-primary">{progress?.cards_mastered || 0}/78 cards mastered</span>
              </div>
              <Progress value={(progress?.cards_mastered || 0) / 78 * 100} className="h-3 bg-muted" />
              <p className="text-xs text-muted-foreground text-center">
                {78 - (progress?.cards_mastered || 0)} cards remaining to master the full Rider-Waite deck
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Riddle of the Day */}
      <Card className="border-accent/30">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-accent" />
            <CardTitle>Quiz Time</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground mb-4">
            Test your knowledge with questions about {studiedCards.length} card{studiedCards.length > 1 ? 's' : ''}
          </p>
          <Button 
            onClick={startQuiz} 
            className="w-full gap-2 bg-gradient-to-r from-accent to-soft-accent hover:from-soft-accent hover:to-accent text-background"
          >
            <Play className="h-4 w-4" />
            Start Quiz
          </Button>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-4">
        <Button 
          variant="outline" 
          className="h-24 flex-col gap-2"
          onClick={() => setShowDeckLibrary(true)}
        >
          <BookOpen className="h-6 w-6" />
          <span>Full Deck</span>
        </Button>
        <Button 
          variant="outline" 
          className="h-24 flex-col gap-2"
          onClick={() => setShowSpreadJournal(true)}
        >
          <Sparkles className="h-6 w-6" />
          <span>Spreads & Journal</span>
        </Button>
      </div>

      {/* Community Practice Section */}
      <Card className="bg-gradient-to-br from-accent/10 to-primary/10 border-accent/30">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-accent" />
            <CardTitle className="text-lg">Practice with the Community</CardTitle>
          </div>
          <CardDescription>
            Connect with fellow tarot learners and intuitive practitioners
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Learning tarot is so much more powerful with others! Join the Connected Collective to:
          </p>
          <ul className="text-sm text-muted-foreground space-y-2 ml-4">
            <li>• Practice readings with other learners</li>
            <li>• Share interpretations and get feedback</li>
            <li>• Ask questions about card meanings</li>
            <li>• Connect with experienced readers</li>
            <li>• Explore intuitive development together</li>
          </ul>
          <Button 
            className="w-full gap-2 bg-gradient-to-r from-accent to-primary hover:from-primary hover:to-accent"
            onClick={() => window.location.href = '/connect-and-grow#community'}
          >
            <Trophy className="h-4 w-4" />
            Join the Tarot & Intuition Community
          </Button>
        </CardContent>
      </Card>

      {/* Dialogs */}
      {showQuiz && studiedCards.length > 0 && (
        <TarotCardQuiz
          cards={studiedCards}
          onComplete={handleQuizComplete}
          onClose={() => setShowQuiz(false)}
        />
      )}

      {showCardDetail && currentCard && (
        <TarotCardDetail
          card={currentCard}
          onClose={() => setShowCardDetail(false)}
          onMastered={progressToNextCard}
        />
      )}

      {showDeckLibrary && (
        <TarotDeckLibrary
          onClose={() => setShowDeckLibrary(false)}
          onSelectCard={(card) => {
            setCurrentCard(card);
            setShowDeckLibrary(false);
            setShowCardDetail(true);
          }}
        />
      )}

      {showSpreadJournal && (
        <TarotSpreadJournal
          onClose={() => setShowSpreadJournal(false)}
        />
      )}

      {/* Mastered Cards Dialog */}
      <Dialog open={showMasteredCards} onOpenChange={setShowMasteredCards}>
        <DialogContent className="max-w-4xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-primary" />
              Cards You've Mastered
            </DialogTitle>
            <DialogDescription>
              {masteredCards.length} of 78 cards completed - keep up the great work!
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[calc(85vh-10rem)] pr-4">
            {masteredCards.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No cards mastered yet. Keep studying to see your progress here!</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 py-2">
                {masteredCards.map((card) => (
                  <div key={card.id} className="flex flex-col items-center gap-2 animate-fade-in hover:scale-105 transition-transform">
                    <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-gradient-to-br from-primary/5 to-accent/5 flex items-center justify-center border-2 border-primary/20 hover:border-primary/40 cursor-pointer">
                      {card.image_url ? (
                        <img src={card.image_url} alt={card.card_name} className="w-full h-full object-contain" crossOrigin="anonymous" />
                      ) : (
                        <Sparkles className="h-8 w-8 text-muted-foreground" />
                      )}
                    </div>
                    <p className="text-xs text-center font-medium">{card.card_name}</p>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Studied Cards Dialog */}
      <Dialog open={showStudiedCardsList} onOpenChange={setShowStudiedCardsList}>
        <DialogContent className="max-w-4xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="h-5 w-5 text-accent" />
              Cards You're Studying
            </DialogTitle>
            <DialogDescription>
              {studiedCards.length} cards currently in your study rotation
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[calc(85vh-10rem)] pr-4">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 py-2">
              {studiedCards.map((card) => (
                <div key={card.id} className="flex flex-col items-center gap-2 animate-fade-in hover:scale-105 transition-transform">
                  <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-gradient-to-br from-accent/5 to-primary/5 flex items-center justify-center border-2 border-accent/20 hover:border-accent/40 cursor-pointer">
                    {card.image_url ? (
                      <img src={card.image_url} alt={card.card_name} className="w-full h-full object-contain" crossOrigin="anonymous" />
                    ) : (
                      <Sparkles className="h-8 w-8 text-muted-foreground" />
                    )}
                  </div>
                  <p className="text-xs text-center font-medium">{card.card_name}</p>
                </div>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
